import { propPath } from './propManifest';
import { ALL_SAVERS, type PowerSavers } from './powerSavers';

// Graphics tiers (D186, retiered D187 to the user's spec) — the decimation ladder as data.
//
// WHY THIS EXISTS. The scatter props and the harbour shipped UNDECIMATED: a wheat sprout
// ~50 device pixels tall carried 3,995 triangles and stood on the board 264 times, and the
// port carried 93,702 triangles nine times. Measured on an idle board that was 2,997,374 of
// the 3,367,936 triangles in a single pass — 89% of the board's geometry — and the frame
// runs that geometry SIX times (shadow ×3, refraction, reflection, canvas).
//
// The ratio that fixes it is not an arithmetic question, it is a LOOKING question, and this
// project has learned that lesson expensively (D38, D50, D95, D98). So the ladder is data,
// the shipped default is one rung of it, and `pnpm gen-lods` writes the other rungs where
// the Settings panel can load them — the player picks a tier and judges it on the real board.
//
// PURE: no three.js, no DOM, no fetch. Just the ladder and the URL arithmetic, so the
// mapping from "which tier" to "which file" is a unit test rather than a thing you find out
// by staring at a bare board.

/** The tiers, cheapest first — the Settings slider renders them in this order. */
export type DetailLevel = 'common' | 'fine' | 'utmost';

export const DETAIL_LEVELS: readonly DetailLevel[] = Object.freeze(['common', 'fine', 'utmost'] as const);

export interface DetailSpec {
  /** Shown on the slider. */
  label: string;
  /** gltfpack `-si` ratio applied to the scatter props. */
  propRatio: number;
  /** gltfpack `-si` ratio applied to `items/port`. 1 leaves the harbour alone. */
  portRatio: number;
  /**
   * Share of the flock's sheep that survives (D277). 1 is the original export; below it the
   * sheep is REBUILT rather than squeezed — see `tools/models/rampLod.mjs` — because its UV
   * atlas has a seam at nearly every vertex, and no simplifier can cross those without
   * smearing the wool across the face.
   */
  flockRatio: number;
  /**
   * Whether this tier asks for the sun's shadow map.
   *
   * The DEVICE still has the final say — the phone budget allocates no shadow map at all
   * (`SHADOW_MAP_SIZE` in stage.ts), so Utmost on a phone is still shadowless. This flag is
   * the player's half of that decision, never the whole of it.
   */
  shadows: boolean;
  /**
   * D294: which power savers this tier runs (render/powerSavers.ts). Every tier runs all five
   * today; they are per tier so one can be dropped where it costs a look the tier is for.
   */
  savers: Readonly<PowerSavers>;
  /** One line the panel shows under the slider. */
  note: string;
}

/**
 * The ladder.
 *
 * `common` is the SHIPPED tier and the one the canonical asset paths hold — the cheapest
 * rung is the default deliberately, because a fresh clone and a phone should both land on
 * the tier that cannot hurt them.
 *
 * COMMON SITS ON ITS FREE CEILING, and the ceiling was measured rather than guessed (D188).
 * gltfpack's aggressive simplifier cannot collapse below a mesh's own topology, so the props
 * FLOOR at ~450,300 board triangles and stay there no matter how hard they are squeezed:
 *
 *     ratio   0.04     0.08     0.12     0.15     0.20     0.22     0.25     0.30
 *     props   450,327  450,453  451,137  456,565  474,244  491,913  520,586  625,504
 *
 * Everything up to 0.20 is within ~5% of the floor — asking for five times the ratio costs
 * five percent. From 0.22 the curve turns up and never comes back. So 0.20 is the most shape
 * the cheapest tier can carry for the floor's price, which is exactly what it is set to.
 *
 * THE HARBOUR HAS NO SUCH CEILING: it is linear in the ratio across the whole range
 * (14,017 @ 0.15 → 18,740 @ 0.2 → 28,106 @ 0.3 → 93,702 @ 1.0), because it is one mesh of
 * many separate shells rather than a floored blob. It is left at 0.2, which was checked by
 * eye; at an equivalent of ~0.05 its hulls and masts come apart outright — the same failure
 * `tools/models/optimize.mjs` already records for the wheat and wood tiles.
 *
 * The three tiers are then spaced roughly evenly in what they actually COST, not in their
 * ratios: 642,904 → 1,544,088 → 3,021,556 board triangles, each step about 2.2×.
 *
 * THE FLOCK IS THE THIRD DIAL (D277), and until then it was not on the ladder at all: nineteen
 * 8,740-triangle sheep were 166,060 triangles on EVERY tier — 19% of the Common board, more than
 * the nine harbours. It needs its own dial because it fails differently again: a
 * skinned animal whose texture atlas is seams everywhere. Chosen at the board's CLOSEST zoom,
 * where a sheep is ~65 device pixels: 0.2 (1,748 triangles) is the same sheep with softer wool;
 * 0.1 (896) starts to read crumpled; 0.5 (4,374) keeps even the eye.
 */
export const DETAIL: Readonly<Record<DetailLevel, DetailSpec>> = Object.freeze({
  common: Object.freeze({ savers: ALL_SAVERS, label: 'Common', propRatio: 0.2, portRatio: 0.2, flockRatio: 0.2, shadows: false, note: 'Lightest. What ships, and what a phone gets.' }),
  fine: Object.freeze({ savers: ALL_SAVERS, label: 'Fine', propRatio: 0.5, portRatio: 0.6, flockRatio: 0.5, shadows: false, note: 'Half the original geometry — most of its shape.' }),
  utmost: Object.freeze({ savers: ALL_SAVERS, label: 'Utmost', propRatio: 1, portRatio: 1, flockRatio: 1, shadows: true, note: 'The original exports, plus the sun’s shadows. Costs the most.' }),
});

/** The flock's model — the one rigged, animated file on the ladder (D277). */
export const FLOCK_MODEL = propPath('sheep');

/** The tier the game ships with, and the one the canonical asset paths hold. */
export const DEFAULT_DETAIL: DetailLevel = 'common';

/** Subdirectory under MODEL_BASE holding the non-default tiers (tracked since D214; `pnpm gen-lods` rebuilds it). */
export const LOD_DIR = '_lods';

/**
 * Which model paths the ladder actually covers.
 *
 * Only the three that were measured as a problem: the scatter props and the harbour (D186),
 * and the flock (D277), which is ONE file standing on the board nineteen times. Buildings, the
 * robber and the shelf item models are ~3 instances each or off-board entirely, so decimating
 * them buys ~96k triangles against a real risk to the seat-coloured roofs — which are the thin
 * geometry that does not survive it, as `tools/models/optimize.mjs` already records.
 */
export function laddered(path: string): boolean {
  return path.startsWith('props/') || path === 'items/port' || path === FLOCK_MODEL;
}

/**
 * Resolve a model path to a URL for the chosen tier.
 *
 * The DEFAULT tier is the canonical path, so a fresh clone that has never run `pnpm gen-lods`
 * can only ever miss files by an explicit choice above Common — never by default. That matters
 * more than it looks: a missing `.glb` does not 404 into a rejected promise, it 200s into the
 * SPA's HTML fallback and dies as a parse error (D41), so the failure mode is a bare board.
 */
export function modelUrl(base: string, path: string, level: DetailLevel = DEFAULT_DETAIL): string {
  if (level === DEFAULT_DETAIL || !laddered(path)) return `${base}/${path}.glb`;
  return `${base}/${LOD_DIR}/${level}/${path}.glb`;
}

/** Parse a stored/URL value back to a tier; anything unrecognised falls to the default. */
export function asDetailLevel(raw: unknown): DetailLevel {
  return typeof raw === 'string' && (DETAIL_LEVELS as readonly string[]).includes(raw) ? (raw as DetailLevel) : DEFAULT_DETAIL;
}

/** Slider position (0 = cheapest) ⇄ tier, for the Settings range input. */
export function detailAt(index: number): DetailLevel {
  const i = Math.max(0, Math.min(DETAIL_LEVELS.length - 1, Math.round(index)));
  return DETAIL_LEVELS[i]!;
}
export function detailIndex(level: DetailLevel): number {
  const i = DETAIL_LEVELS.indexOf(level);
  return i < 0 ? DETAIL_LEVELS.indexOf(DEFAULT_DETAIL) : i;
}
